import React, { useState, useContext, createContext, useEffect } from "react";
import classnames from "classnames";
import styles from "./SearchBox.module.scss";
import { MicroCopyContext } from "@unum/micro-copy";
import AccessibilityLabel from "@unum/accessibility-label";
import Button, { Props as BaseButtonProps } from "@unum/button";
import Clickable from "@unum/clickable";
import SearchIcon from "./assets/search-icon.inline.svg";
import SearchDotIcon from "./assets/search-dot-icon.inline.svg";

type Props = {
  onSubmit: (value: string) => void;
  onChange?: (value: string) => void;
  layout?: "small" | "large";
  backgroundColour?: "grey" | "white";
  children?: React.ReactNode;
  value?: string;
  placeholder?: string;
};

const Context = createContext<Props["layout"]>("small");

const SearchBox = ({
  layout = "small",
  backgroundColour = "grey",
  onSubmit,
  onChange,
  children,
  value,
  placeholder
}: Props) => {
  const getMicroCopy = useContext(MicroCopyContext);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(value);
  const onSearchChange = (term: string) => {
    if (onChange) {
      onChange(term);
    }

    setSearchTerm(term);
  };

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(searchTerm);
  };

  return (
    <form
      className={classnames(
        styles["SearchBox"],
        styles[`SearchBox--${layout}`],
        {
          [styles["SearchBox--white"]]: backgroundColour === "white",
          [styles["SearchBox--hasInlineIcon"]]: children
        }
      )}
      onSubmit={handleSubmit}
    >
      <div
        className={classnames(styles["input-wrapper"], {
          "nav-search": layout === "small",
          "hero-search__nav": layout === "large"
        })}
      >
        <input
          aria-label="Search phrase"
          className={classnames("input", styles["input"], {
            "nav-search__input": layout === "small",
            "hero-search__input": layout === "large"
          })}
          type="text"
          name="q"
          placeholder={placeholder || getMicroCopy("search.label")}
          value={searchTerm || ""}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {!children ? (
          <Clickable
            model="submit"
            aria-label="Search"
            className={classnames("button", styles["submit"], {
              "nav-search__submit": layout === "small" || Boolean(children),
              "hero-search__submit": layout === "large"
            })}
          >
            {layout === "small" && (
              <SearchIcon
                className={classnames(
                  "nav-search__icon",
                  styles["search-icon-inner-small"]
                )}
              />
            )}
            <SearchDotIcon
              className={classnames(styles["search-icon"], {
                "hero-search__icon": layout === "large" && Boolean(!children),
                [classnames("nav-search__icon", "nav-search__icon--mobile")]:
                  layout === "small" || Boolean(children)
              })}
            />
            <AccessibilityLabel>
              {getMicroCopy("search.submit")}
            </AccessibilityLabel>
          </Clickable>
        ) : (
          <SearchIcon className={styles["search-icon-inner"]} />
        )}
      </div>
      <Context.Provider value={layout}>{children}</Context.Provider>
    </form>
  );
};

type ButtonProps = BaseButtonProps;

const SearchBoxButton = ({ children, className, ...rest }: ButtonProps) => {
  const layout = useContext(Context);

  return (
    <Button
      {...rest}
      action={{ model: "submit" }}
      aria-label="submit-button"
      className={classnames(styles["submit-button"], className)}
      theme={layout === "small" ? "thin" : "default"}
    >
      {children}
    </Button>
  );
};

SearchBox.Button = SearchBoxButton;

export default SearchBox;
